/**
 * Animated Owl Mascot Component
 *
 * A friendly owl mascot that can be used across the app
 * Features animated eyes, blinking, hover effects with big eyes
 */

import { useState, useEffect } from 'react';
import './OwlMascot.css';

interface OwlMascotProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  showBubbleOnHover?: boolean;
}

export function OwlMascot({ message, size = 'medium', onClick, showBubbleOnHover = true }: OwlMascotProps) {
  const [isBlinking, setIsBlinking] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Random blinking effect (disabled when hovered - eyes are wide open!)
  useEffect(() => {
    if (isHovered) return; // Don't blink when hovered

    const blinkInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        setIsBlinking(true);
        setTimeout(() => setIsBlinking(false), 150);
      }
    }, 2000);

    return () => clearInterval(blinkInterval);
  }, [isHovered]);

  const sizeMap = {
    small: 50,
    medium: 70,
    large: 100,
  };

  const mascotSize = sizeMap[size];

  // Eye size: bigger when hovered (surprised look!)
  const normalEyeRy = 10;
  const hoveredEyeRy = 13;
  const eyeRy = isBlinking ? 1 : (isHovered ? hoveredEyeRy : normalEyeRy);

  // Pupil size: bigger when hovered
  const normalPupilR = 3;
  const hoveredPupilR = 4;
  const pupilR = isHovered ? hoveredPupilR : normalPupilR;

  return (
    <div
      className={`owl-mascot owl-mascot--${size} ${isHovered ? 'owl-mascot--hovered' : ''}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Bubble only shows on hover */}
      {showBubbleOnHover && message && isHovered && (
        <div className="owl-mascot__bubble owl-mascot__bubble--hover">{message}</div>
      )}
      <svg
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        width={mascotSize}
        height={mascotSize}
        className="owl-mascot__svg"
      >
        <defs>
          <linearGradient id="owlBodyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="owlEyeGlow" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>

        {/* Wings */}
        <ellipse className="owl-mascot__wing owl-mascot__wing--left" cx="22" cy="55" rx="12" ry="20" fill="#7c3aed" opacity="0.8" />
        <ellipse className="owl-mascot__wing owl-mascot__wing--right" cx="78" cy="55" rx="12" ry="20" fill="#7c3aed" opacity="0.8" />

        {/* Body */}
        <ellipse className="owl-mascot__body" cx="50" cy="55" rx="32" ry="35" fill="url(#owlBodyGradient)" />

        {/* Belly */}
        <ellipse cx="50" cy="62" rx="20" ry="22" fill="#c4b5fd" opacity="0.5" />

        {/* Face circle */}
        <circle cx="50" cy="40" r="28" fill="#ede9fe" />

        {/* Eye backgrounds - bigger on hover */}
        <ellipse
          cx="38"
          cy="38"
          rx={isHovered ? 14 : 12}
          ry={isHovered ? 16 : 14}
          fill="url(#owlEyeGlow)"
          style={{ transition: 'rx 0.2s ease, ry 0.2s ease' }}
        />
        <ellipse
          cx="62"
          cy="38"
          rx={isHovered ? 14 : 12}
          ry={isHovered ? 16 : 14}
          fill="url(#owlEyeGlow)"
          style={{ transition: 'rx 0.2s ease, ry 0.2s ease' }}
        />

        {/* Eyes - bigger when hovered */}
        <ellipse
          className="owl-mascot__eye"
          cx="38"
          cy="38"
          rx={isHovered ? 10 : 8}
          ry={eyeRy}
          fill="#1e1b4b"
          style={{ transition: 'rx 0.2s ease, ry 0.15s ease' }}
        />
        <ellipse
          className="owl-mascot__eye"
          cx="62"
          cy="38"
          rx={isHovered ? 10 : 8}
          ry={eyeRy}
          fill="#1e1b4b"
          style={{ transition: 'rx 0.2s ease, ry 0.15s ease' }}
        />

        {/* Pupils - hidden when blinking, bigger when hovered */}
        {!isBlinking && (
          <>
            <circle
              className="owl-mascot__pupil"
              cx="40"
              cy="38"
              r={pupilR}
              fill="white"
              style={{ transition: 'r 0.2s ease' }}
            />
            <circle
              className="owl-mascot__pupil"
              cx="64"
              cy="38"
              r={pupilR}
              fill="white"
              style={{ transition: 'r 0.2s ease' }}
            />
          </>
        )}

        {/* Beak */}
        <path d="M 45 48 L 50 58 L 55 48 Z" fill="#f59e0b" />

        {/* Eyebrows / Tufts */}
        <path d="M 25 22 Q 35 18 38 25" stroke="#7c3aed" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M 75 22 Q 65 18 62 25" stroke="#7c3aed" strokeWidth="3" fill="none" strokeLinecap="round" />

        {/* Ear tufts */}
        <path d="M 28 15 L 32 25 L 25 22 Z" fill="#8b5cf6" />
        <path d="M 72 15 L 68 25 L 75 22 Z" fill="#8b5cf6" />

        {/* Sparkles */}
        <g className="owl-mascot__sparkle" style={{ transformOrigin: '15px 20px' }}>
          <path d="M 15 15 L 15 25 M 10 20 L 20 20" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
        </g>
        <g className="owl-mascot__sparkle" style={{ transformOrigin: '85px 25px' }}>
          <path d="M 85 20 L 85 30 M 80 25 L 90 25" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
        </g>

        {/* Feet */}
        <ellipse cx="40" cy="88" rx="8" ry="4" fill="#f59e0b" />
        <ellipse cx="60" cy="88" rx="8" ry="4" fill="#f59e0b" />
      </svg>
    </div>
  );
}
